/**
 * NHTSA vPIC VIN Decoder Service
 * Free US Government API for decoding Vehicle Identification Numbers
 * API Documentation: https://vpic.nhtsa.dot.gov/api/
 */

export interface VinDecodedData {
  year: number | null;
  make: string;
  model: string;
  /** NHTSA "Series" — often the platform name (e.g. "3-Series" when Model is "M340i"). */
  series: string;
  /** NHTSA "Series2" — secondary platform/variant qualifier. */
  series2: string;
  trim: string;
  /** NHTSA "Trim2" — secondary trim qualifier; sometimes populated when Trim is empty. */
  trim2: string;
  bodyStyle: string;
  driveType: string;
  fuelType: string;
  transmission: string;
  engineCylinders: string;
  engineDisplacement: string;
  engineDescription: string;
  /** Doors as reported by NHTSA when present. */
  doors: string;
  errorCode: string;
  errorText: string;
  isValid: boolean;
}

interface NHTSAResult {
  Variable: string;
  Value: string | null;
  ValueId: string | null;
}

interface NHTSAResponse {
  Count: number;
  Message: string;
  SearchCriteria: string;
  Results: NHTSAResult[];
}

const NHTSA_API_BASE = "https://vpic.nhtsa.dot.gov/api/vehicles";

/**
 * Validates VIN format (17 characters, no I, O, Q)
 */
export function isValidVinFormat(vin: string): boolean {
  if (!vin || vin.length !== 17) return false;
  // VINs cannot contain I, O, or Q
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/i;
  return vinRegex.test(vin);
}

// ISO 3779 transliteration table for the check-digit calculation.
const VIN_CHAR_VALUES: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
  "0": 0, "1": 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9,
};
const VIN_POSITION_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Whether the ISO 3779 check digit (position 9) is *mandatory* for this VIN.
 *
 * The check digit is required for vehicles manufactured for North America
 * (WMI first character 1–5) and China ("L"). Europe (S–Z) and several other
 * regions do not mandate it, so a number of legitimate European VINs fail the
 * calculation. Policy decision (MVP-2.2): **hard-block** a failing check digit
 * only when it's required; otherwise treat failure as a **soft warning** and
 * still let NHTSA attempt the decode.
 */
export function isCheckDigitRequired(vin: string): boolean {
  if (!vin) return false;
  return /^[1-5L]/i.test(vin);
}

/**
 * Validates the ISO 3779 check digit (position 9). North-American VINs are
 * required to use this scheme; many European-built vehicles still satisfy it
 * even though it isn't strictly mandatory there. Use together with
 * `isCheckDigitRequired` to decide whether a failure is a hard block or a soft
 * warning.
 */
export function isValidVinCheckDigit(vin: string): boolean {
  if (!isValidVinFormat(vin)) return false;
  const upper = vin.toUpperCase();
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = upper[i];
    const value = VIN_CHAR_VALUES[ch];
    if (value === undefined) return false;
    sum += value * VIN_POSITION_WEIGHTS[i];
  }
  const remainder = sum % 11;
  const expected = remainder === 10 ? "X" : String(remainder);
  return upper[8] === expected;
}

/**
 * Extracts a value from NHTSA results by variable name
 */
function getValueByVariable(results: NHTSAResult[], variableName: string): string {
  const result = results.find((r) => r.Variable === variableName);
  return result?.Value?.trim() || "";
}

/**
 * Formats raw displacement value to user-friendly display (e.g., 4.163000 -> 4.2)
 */
function formatDisplacement(rawDisplacement: string): string {
  const value = parseFloat(rawDisplacement);
  if (isNaN(value)) return rawDisplacement;
  
  const rounded = Math.round(value * 10) / 10;
  return rounded.toString();
}

/**
 * Formats cylinder count with engine configuration (e.g., 8 + V-Shaped -> V8)
 */
function formatCylinders(cylinderCount: string, config: string): string {
  if (!cylinderCount) return "";
  
  const configMap: Record<string, string> = {
    "V-Shaped": "V",
    "In-Line": "I",
    "Flat": "H",
    "W-Shaped": "W",
    "Rotary": "Rotary",
  };
  
  const prefix = configMap[config];
  if (prefix) {
    return `${prefix}${cylinderCount}`;
  }
  
  return `${cylinderCount}-Cylinder`;
}

/**
 * Formats transmission style to user-friendly display (e.g., "Manual" or "Automatic")
 */
function formatTransmission(rawTransmission: string): string {
  if (!rawTransmission) return "";
  
  const lower = rawTransmission.toLowerCase();
  
  if (lower.includes("manual") || lower.includes("mt")) {
    return "Manual";
  }
  if (lower.includes("automatic") || lower.includes("auto") || lower.includes("cvt") || lower.includes("at")) {
    return "Automatic";
  }
  if (lower.includes("dual") || lower.includes("dct") || lower.includes("dsg") || lower.includes("pdk")) {
    return "Automatic (Dual Clutch)";
  }
  
  return rawTransmission;
}

/** Default NHTSA decode timeout. vPIC is usually <1s but can stall; we cap the
 *  wait so the wizard can offer a retry instead of hanging indefinitely. */
export const DECODE_TIMEOUT_MS = 10000;

/**
 * Decodes a VIN using the NHTSA vPIC API.
 *
 * @param vin        17-char VIN.
 * @param options.timeoutMs  Abort the request after this many ms (default 10s);
 *                           a timeout surfaces as a TIMEOUT errorCode so the UI
 *                           can show a retry affordance.
 */
export async function decodeVin(
  vin: string,
  options: { timeoutMs?: number } = {}
): Promise<VinDecodedData> {
  const timeoutMs = options.timeoutMs ?? DECODE_TIMEOUT_MS;
  const defaultResult: VinDecodedData = {
    year: null,
    make: "",
    model: "",
    series: "",
    series2: "",
    trim: "",
    trim2: "",
    bodyStyle: "",
    driveType: "",
    fuelType: "",
    transmission: "",
    engineCylinders: "",
    engineDisplacement: "",
    engineDescription: "",
    doors: "",
    errorCode: "",
    errorText: "",
    isValid: false,
  };

  if (!isValidVinFormat(vin)) {
    return {
      ...defaultResult,
      errorCode: "INVALID_FORMAT",
      errorText: "VIN must be exactly 17 characters and cannot contain I, O, or Q",
    };
  }

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer =
    controller && timeoutMs > 0
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

  try {
    const response = await fetch(
      `${NHTSA_API_BASE}/decodevin/${vin.toUpperCase()}?format=json`,
      controller ? { signal: controller.signal } : undefined
    );

    if (!response.ok) {
      return {
        ...defaultResult,
        errorCode: "API_ERROR",
        errorText: `API returned status ${response.status}`,
      };
    }

    const data: NHTSAResponse = await response.json();

    if (!data.Results || data.Results.length === 0) {
      return {
        ...defaultResult,
        errorCode: "NO_RESULTS",
        errorText: "No vehicle data found for this VIN",
      };
    }

    const results = data.Results;

    // Check for error codes from NHTSA
    const errorCode = getValueByVariable(results, "Error Code");
    const errorText = getValueByVariable(results, "Error Text");

    // Error codes: 0 = no error, 1-10 = various errors
    // Some error codes still return partial data
    const hasErrors = errorCode && errorCode !== "0" && !errorCode.includes("0");

    const yearStr = getValueByVariable(results, "Model Year");
    const year = yearStr ? parseInt(yearStr, 10) : null;

    const make = getValueByVariable(results, "Make");
    const model = getValueByVariable(results, "Model");

    // Anti-scam hard gate: a "real car" must have year + make + model from
    // NHTSA. We surface a single, generic error to keep the UX consistent
    // (see plans/nhtsa-normalization-ux.md). Trim/series may be empty —
    // the catalog matcher handles that downstream.
    if (!year || !make || !model) {
      return {
        ...defaultResult,
        errorCode: errorCode || "INCOMPLETE_DATA",
        errorText: "We could not find a vehicle associated with this VIN.",
        isValid: false,
      };
    }

    // Build engine description
    const cylinders = getValueByVariable(results, "Engine Number of Cylinders");
    const displacement = getValueByVariable(results, "Displacement (L)");
    const engineConfig = getValueByVariable(results, "Engine Configuration");
    
    let engineDescription = "";
    if (cylinders && displacement) {
      const formattedDisp = formatDisplacement(displacement);
      const formattedCyl = formatCylinders(cylinders, engineConfig);
      engineDescription = `${formattedDisp}L ${formattedCyl}`;
    } else if (cylinders) {
      engineDescription = formatCylinders(cylinders, engineConfig);
    } else if (displacement) {
      engineDescription = `${formatDisplacement(displacement)}L`;
    }

    // NHTSA sometimes returns the literal string "NA" instead of an empty
    // string for missing values. Treat "NA" as empty everywhere downstream.
    const stripNa = (v: string) =>
      v && v.trim().toUpperCase() === "NA" ? "" : v;

    return {
      year,
      make,
      model,
      series: stripNa(getValueByVariable(results, "Series")),
      series2: stripNa(getValueByVariable(results, "Series2")),
      trim: stripNa(getValueByVariable(results, "Trim")),
      trim2: stripNa(getValueByVariable(results, "Trim2")),
      bodyStyle: getValueByVariable(results, "Body Class"),
      driveType: getValueByVariable(results, "Drive Type"),
      fuelType: getValueByVariable(results, "Fuel Type - Primary"),
      transmission: formatTransmission(getValueByVariable(results, "Transmission Style")),
      engineCylinders: cylinders,
      engineDisplacement: displacement,
      engineDescription,
      doors: getValueByVariable(results, "Doors"),
      errorCode: hasErrors ? errorCode : "",
      errorText: hasErrors ? errorText : "",
      isValid: true,
    };
  } catch (error) {
    const aborted =
      error instanceof Error &&
      (error.name === "AbortError" || error.name === "TimeoutError");
    if (aborted) {
      return {
        ...defaultResult,
        errorCode: "TIMEOUT",
        errorText: "The VIN lookup timed out. Please try again.",
      };
    }
    console.error("VIN decode error:", error);
    return {
      ...defaultResult,
      errorCode: "NETWORK_ERROR",
      errorText: "Failed to connect to VIN decoder service",
    };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Format VIN for display (uppercase, trimmed)
 */
export function formatVin(vin: string): string {
  return vin.toUpperCase().trim();
}
